import { useState } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useNavigate, Link } from 'react-router-dom';
import { Button, Input, Card } from '../common';

interface RegisterFormProps {
  initialReferralCode?: string;
}

export function EnhancedRegisterForm({ initialReferralCode = '' }: RegisterFormProps) {
  // Enhanced registration form v2.0 with validation and UX improvements
  const [email, setEmail] = useState('');
  const [nickname, setNickname] = useState('');
  const [password, setPassword] = useState('');
  const [referralCode, setReferralCode] = useState(initialReferralCode.toUpperCase());
  const [showPassword, setShowPassword] = useState(false);
  const [hasViewedPassword, setHasViewedPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const { register, isLoading, error, clearError } = useAuthStore();
  const navigate = useNavigate();

  // Email validation
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Password validation
  const isValidPassword = (password: string) => {
    const hasMinLength = password.length >= 8;
    const hasLowerCase = /[a-z]/.test(password);
    const hasUpperCase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    return hasMinLength && hasLowerCase && hasUpperCase && hasNumber;
  };

  // Check if form is valid for submission
  const isFormValid = () => {
    return isValidEmail(email) && isValidPassword(password) && hasViewedPassword;
  };

  // Handle password visibility toggle
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
    if (!hasViewedPassword) {
      setHasViewedPassword(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError('');
    
    // Final validation
    if (!isValidEmail(email)) {
      setLocalError('Please enter a valid email address');
      return;
    }
    
    if (!isValidPassword(password)) {
      setLocalError('Password must be at least 8 characters with lowercase, uppercase, and number');
      return;
    }
    
    if (!hasViewedPassword) {
      setLocalError('Please view your password at least once before signing up');
      return;
    }
    
    try {
      // Only include nickname and referral code if provided
      const registerData: any = { email, password };
      if (nickname.trim()) {
        registerData.nickname = nickname.trim();
      }
      if (referralCode.trim()) {
        registerData.referralCode = referralCode.trim().toUpperCase();
      }
      await register(registerData);
      navigate('/app');
    } catch (err) {
      // Error is handled by store
    }
  };

  const displayError = localError || error;

  return (
    <div className="w-full max-w-md">
      <Card variant="elevated">
        <h1 className="text-3xl font-bold text-white mb-6 text-center">
          Create Account
        </h1>
        
        {displayError && (
          <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded mb-4">
            {displayError}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email Field with Validation */}
          <div>
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              disabled={isLoading}
              fullWidth
            />
            {email && (
              <p className={`text-sm mt-1 ${isValidEmail(email) ? 'text-green-500' : 'text-red-500'}`}>
                {isValidEmail(email) ? '✓ Valid email format' : '✗ Please enter a valid email address'}
              </p>
            )}
          </div>
          
          <Input
            label="Nickname (Optional)"
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="How should the AI address you?"
            disabled={isLoading}
            fullWidth
          />
          
          <Input
            label="Referral Code (Optional)"
            type="text"
            value={referralCode}
            onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
            placeholder="Enter friend's referral code"
            disabled={isLoading}
            fullWidth
            maxLength={20}
          />
          {referralCode && (
            <p className="text-xs text-gray-400 -mt-2">
              💝 You and your friend will both receive rewards!
            </p>
          )}
          
          {/* Password Field with Show/Hide and Validation */}
          <div>
            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                disabled={isLoading}
                minLength={8}
                fullWidth
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute right-3 top-9 text-gray-400 hover:text-white focus:outline-none"
                disabled={isLoading}
              >
                👁️
              </button>
            </div>
            {password && (
              <div className="mt-1 space-y-1">
                <p className={`text-sm ${isValidPassword(password) ? 'text-green-500' : 'text-red-500'}`}>
                  {isValidPassword(password) ? '✓ Password meets all requirements' : '✗ Password requirements:'}
                </p>
                {!isValidPassword(password) && (
                  <ul className="text-xs text-red-500 ml-4 space-y-1">
                    <li className={password.length >= 8 ? 'text-green-500' : ''}>
                      {password.length >= 8 ? '✓' : '✗'} Minimum 8 characters
                    </li>
                    <li className={/[a-z]/.test(password) ? 'text-green-500' : ''}>
                      {/[a-z]/.test(password) ? '✓' : '✗'} At least one lowercase letter (a-z)
                    </li>
                    <li className={/[A-Z]/.test(password) ? 'text-green-500' : ''}>
                      {/[A-Z]/.test(password) ? '✓' : '✗'} At least one uppercase letter (A-Z)
                    </li>
                    <li className={/[0-9]/.test(password) ? 'text-green-500' : ''}>
                      {/[0-9]/.test(password) ? '✓' : '✗'} At least one number (0-9)
                    </li>
                  </ul>
                )}
                {password && !hasViewedPassword && (
                  <p className="text-sm text-yellow-500">
                    ⚠️ Please click the 👁️ icon to view your password before signing up
                  </p>
                )}
                {hasViewedPassword && (
                  <p className="text-sm text-green-500">
                    ✓ Password has been viewed
                  </p>
                )}
              </div>
            )}
          </div>
          
          <Button
            type="submit"
            isLoading={isLoading}
            disabled={!isFormValid() || isLoading}
            fullWidth
            size="lg"
          >
            {isLoading ? 'Creating account...' : 'Sign Up'}
          </Button>
        </form>
        
        <div className="mt-6 text-center">
          <p className="text-gray-400">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-500 hover:text-primary-400">
              Login
            </Link>
          </p>
        </div>
      </Card>
    </div>
  );
}

// Export as both named and default for compatibility
export { EnhancedRegisterForm as RegisterForm };
export default EnhancedRegisterForm;
